import dayjs, { type Dayjs } from 'dayjs';
import * as z from 'zod';
import { ModelTypeWithEvent } from '../@types';
import { FINPSP_CURRENCY } from '../constants/finpsp';
import {
  AFFILIATE_BONUS_CATEGORY_OPTIONS,
  AFFILIATE_BONUS_REWARD_TYPE_OPTIONS,
  BONUS_PRODUCT_TYPE_OPTIONS,
  BONUS_STATUS_OPTIONS_CUSTOM,
  BONUS_STATUS_OPTIONS_ONGOING,
  BONUS_TIMEFRAME_OPTIONS,
  WELCOME_BONUS_PRODUCT_TYPE_OPTIONS,
  WELCOME_BONUS_SCOPE_OPTIONS,
  WELCOME_BONUS_VALIDITY_PERIOD_OPTIONS,
  VALIDITY_PERIOD_OPTIONS,
  CURRENCY_ENUM,
  CUSTOMER_TASK_MESSAGE_TYPES,
  GENDER_ENUM,
  MODEL_TYPES_WITH_EVENT,
} from '../constants';
import {
  APPROVAL_MODE,
  COMPETITIONS_FLAG_VARIANT,
  MARKET_TYPE_DISPLAY_TYPE,
  ODDS_FORMAT,
  PAYMENT_GATEWAY,
  REOFFER_ODDS_DISPLAY,
  SPORT_ICON_VARIANT,
  TEAM_ASSETS_VARIANT,
} from '../constants/enums';
import { generateZodEnumIds } from '../utils';
import {
  addCreatePasswordValidation,
  addResetPasswordValidation,
  condititionValueValidator,
  isEighteenOrOlder,
  itemEmailValidator,
  itemIdValidator,
  itemInformationValidator,
  itemNameValidator,
  itemPositionValidator,
  itemShortNameValidator,
  itemStringValidator,
  nonEmptyArray,
  nullableNumberInput,
  nullTransformer,
  phoneValidationPattern,
  undefinedTransformer,
  validateRequiredNumber,
} from './helpers';
import { USER_ROLES, roleRequiresManager } from '../constants/keycloak';

export { createWelcomeBonusEditFormSchema, welcomeBonusCreateFormSchema } from './welcomeBonusFormSchema';
export type { WelcomeBonusCreateFormData, WelcomeBonusEditFormData } from './welcomeBonusFormSchema';
export { wageringConfigurationSchema } from './wageringConfigurationSchema';
export type { WageringConfigurationFormData, WageringFieldsFormData } from './wageringConfigurationSchema';
export { campaignCreateFormSchema, campaignEditFormSchema } from './campaignFormSchema';
export type { CampaignCreateFormData, CampaignEditFormData } from './campaignFormSchema';
export { cashbackBonusFormSchema, cashbackFreeSpinTierSchema } from './cashbackBonusFormSchema';
export type {
  CashbackBonusFormData,
  CashbackBonusFormValues,
  CashbackFreeSpinTierData,
  CashbackFreeSpinTierFormValues,
} from './cashbackBonusFormSchema';
export { ggaFreeSpinsSchema } from './ggaFreeSpinsSchema';
export type { GgaFreeSpinsFormData } from './ggaFreeSpinsSchema';
export { ggaFreeSpinsSettingsSchema } from './ggaFreeSpinsSettingsSchema';
export type { GgaFreeSpinsSettingsFormData } from './ggaFreeSpinsSettingsSchema';

// Campaign validation constants
const MAX_CAMPAIGN_PERCENTAGE = 1_000;
const MAX_CAMPAIGN_AMOUNT = 10_000_000;

const sportSchema = z.object({
  name: itemNameValidator(),
  shortName: itemShortNameValidator(),
  position: itemPositionValidator,
  isActive: z.boolean(),
  icon: z.string(),
});

const eventPartIdsSchema = z.array(z.string()).nonempty({ error: 'At least one event part is required.' });
const sportFormInputSchema = sportSchema.extend({ eventPartIds: eventPartIdsSchema.optional() });

export const sportFormSchema = sportFormInputSchema.pipe(sportSchema.extend({ eventPartIds: eventPartIdsSchema }));

export const editSportFormSchema = sportFormInputSchema.transform(({ eventPartIds: _eventPartIds, ...sport }) => sport);

export type SportFormData = z.infer<typeof sportFormSchema>;

export const filterTotalPaymentsSchema = z.object({
  type: z.string({ error: 'Role is required' }),
  managerIds: z.array(z.string()).optional(),
  agentIds: z.array(z.string()).optional(),
  playerIds: z.array(z.string()).optional(),
  paymentMethod: z.string().optional(),
});

export type FilterTotalPaymentsData = z.infer<typeof filterTotalPaymentsSchema>;

export const filterTotalCreditSchema = z.object({
  paymentMethod: z.string().optional(),
});

export type FilterTotalCreditData = z.infer<typeof filterTotalCreditSchema>;

export const competitionFormSchema = z.object({
  sportId: itemIdValidator('Sport'),
  name: itemNameValidator(),
  shortName: itemShortNameValidator(),
  position: itemPositionValidator,
  isActive: z.boolean(),
});

export type CompetitionFormData = z.infer<typeof competitionFormSchema>;

export const tournamentFormSchema = z.object({
  sportId: itemIdValidator('Sport'),
  competitionId: itemIdValidator('Competition'),
  parentId: z.string().nullable(),
  name: itemNameValidator(100),
  shortName: itemShortNameValidator(25),
  position: itemPositionValidator,
  livePosition: itemPositionValidator,
  isActive: z.boolean(),
  topLeague: z.boolean(),
  topOffer: z.boolean(),
  topOfferPosition: itemPositionValidator,
  featured: z.boolean(),
  information: itemInformationValidator,
});

export type TournamentFormData = z.infer<typeof tournamentFormSchema>;

export const participantFormSchema = z.object({
  sportId: itemIdValidator('Sport'),
  competitionIds: z.array(z.string()).nonempty({ error: 'At least one competition is required.' }),
  tournamentIds: z.array(z.string()).nonempty({ error: 'At least one tournament is required.' }),
  name: itemNameValidator(),
  shortName: itemShortNameValidator(),
  isActive: z.boolean(),
});

export type ParticipantFormData = {
  sportId: string;
  competitionIds: string[];
  tournamentIds: string[];
  name: string;
  shortName: string | null;
  isActive: boolean;
};

export const playerFormSchema = z.object({
  participantIds: z.array(z.string()).min(1, { error: 'At least one participant is required.' }).optional(),
  firstName: itemStringValidator({ label: 'First name', required: false }).nullish(),
  lastName: itemStringValidator({ label: 'Last name', required: false }).nullish(),
  displayName: itemShortNameValidator(100),
  isActive: z.boolean(),
  sportId: itemIdValidator('Sport'),
  competitionIds: z.array(z.string()).min(1, { error: 'At least one competition is required.' }).optional(),
  tournamentIds: z.array(z.string()).min(1, { error: 'At least one tournament is required.' }).optional(),
});

export const editPlayerFormSchema = playerFormSchema.transform(({ sportId: _sportId, ...player }) => player);

export type PlayerFormData = z.infer<typeof playerFormSchema>;

export const outcomeTypeFormSchema = z.object({
  sportId: itemIdValidator('Sport'),
  marketTypeId: itemIdValidator('Market').min(1, { error: 'Market is required.' }),
  name: itemNameValidator(),
  shortName: itemShortNameValidator(),
  description: itemInformationValidator,
  position: itemPositionValidator,
  isActive: z.boolean(),
});

export type OutcomeTypeFormData = z.infer<typeof outcomeTypeFormSchema>;

const specialValueSchema = z.object({
  id: z.number(),
  modelType: z.enum(MODEL_TYPES_WITH_EVENT).nullable().transform(nullTransformer),
});

export const marketTypeFormSchema = z.object({
  name: itemNameValidator(),
  shortName: itemShortNameValidator(),
  sportId: itemIdValidator('Sport'),
  description: itemInformationValidator,
  position: itemPositionValidator,
  isActive: z.boolean(),
  specialValueModelTypes: z.array(specialValueSchema),
  isCollapsedMode: z.boolean(),
  sportEventPartId: z.string().nullable(),
  usedFor: z.array(z.enum(['in_play', 'pre_match'])).nonempty({ error: 'At least one value is required.' }),
  type: z.enum(['match', 'outright', 'match_and_outright'], {
    error: 'Type is required.',
  }),
  displayType: z.enum(MARKET_TYPE_DISPLAY_TYPE).nullable(),
});

export type MarketTypeFormData = Omit<z.infer<typeof marketTypeFormSchema>, 'specialValueModelTypes'> & {
  specialValueModelTypes: { id: number; modelType: ModelTypeWithEvent | null }[];
};

export const bonusMarketTypeExclusionSelectorSchema = z.object({
  sportId: z.string().nullable(),
  usedFor: z.union([z.literal(''), z.enum(['in_play', 'pre_match'])]).nullable(),
  type: z.union([z.literal(''), z.enum(['match', 'outright'])]).nullable(),
});

export type BonusMarketTypeExclusionSelectorFormData = z.infer<typeof bonusMarketTypeExclusionSelectorSchema>;

export const marketGroupsSchema = z
  .object({
    name: itemNameValidator(),
    sportId: itemStringValidator({ label: 'Sport' }),
    marketTypeIds: z.array(z.string()),
    mixedGroupIds: z.array(z.string()).optional(),
    displayType: itemStringValidator({ label: 'Display type' }),
    isActive: z.boolean(),
    usedFor: itemStringValidator({ label: 'Used for' }),
  })
  .refine((data) => data.marketTypeIds.length > 0 || (data.mixedGroupIds && data.mixedGroupIds.length > 0), {
    error: 'At least one market or market mix is required.',
    path: ['marketTypeIds'],
  });

export type MarketGroupsFormData = z.infer<typeof marketGroupsSchema>;

export const marketMixSchema = z.object({
  name: itemNameValidator(),
  sportId: itemStringValidator({ label: 'Sport' }),
  marketTypeIds: z
    .array(z.string())
    .min(2, { error: 'At least 2 markets are required.' })
    .max(3, { error: 'Maximum of 3 markets allowed.' }),
  displayType: itemStringValidator({ label: 'Display type' }),
  isActive: z.boolean(),
  usedFor: itemStringValidator({ label: 'Used for' }),
});

export type MarketMixFormData = z.infer<typeof marketMixSchema>;

export const eventFormSchema = z.object({
  name: itemNameValidator(100),
  sportId: itemIdValidator('Sport'),
  competitionId: itemIdValidator('Competition'),
  tournamentId: itemIdValidator('Tournament'),
  participantIds: z
    .array(z.string({ error: 'Participant is required.' }))
    .nonempty({ error: 'At least one participant is required.' }),
  startDate: z.instanceof(dayjs as unknown as typeof Dayjs, { error: 'Date is required.' }),
  information: itemInformationValidator,
  featured: z.boolean(),
});

export const createEventFormSchema = eventFormSchema
  .omit({ name: true })
  .transform(({ competitionId: _competitionId, sportId: _sportId, ...event }) => event);
export const editEventFormSchema = eventFormSchema.pick({
  name: true,
  startDate: true,
  information: true,
  featured: true,
});

export type EditEventFormData = z.infer<typeof editEventFormSchema>;
export type EventFormData = {
  name: string;
  sportId: string;
  competitionId: string;
  tournamentId: string;
  participantIds: string[];
  startDate: Dayjs;
  information: string | null;
  featured: boolean;
};

export const outrightFormSchema = z.object({
  name: itemNameValidator(100),
  sportId: itemIdValidator('Sport'),
  competitionId: itemIdValidator('Competition'),
  tournamentId: itemIdValidator('Tournament'),
  startDate: z.instanceof(dayjs as unknown as typeof Dayjs, { error: 'Date is required.' }),
  information: itemInformationValidator,
});

export const createOutrightFormSchema = outrightFormSchema.transform(
  ({ competitionId: _competitionId, sportId: _sportId, ...outright }) => outright
);
export const editOutrightFormSchema = outrightFormSchema.pick({
  name: true,
  startDate: true,
  information: true,
});

export type EditOutrightFormData = z.infer<typeof editOutrightFormSchema>;
export type OutrightFormData = z.infer<typeof outrightFormSchema>;

const baseCustomerSchema = (transformer: (item: string | null) => string | null | undefined) =>
  z.object({
    firstName: itemStringValidator({ label: 'First name', required: false, max: 255, transformer }).nullish(),
    lastName: itemStringValidator({ label: 'Last name', required: false, max: 255, transformer }).nullish(),
    email: z
      .union([z.literal(''), itemEmailValidator])
      .nullable()
      .transform(undefinedTransformer),
    dateOfBirth: z
      .custom<Dayjs>(
        (date) => {
          if (!dayjs.isDayjs(date)) {
            return false;
          }
          return isEighteenOrOlder(date);
        },
        { error: 'Must be at least 18 years old.' }
      )
      .nullish(),
    phone: z
      .union([z.literal(''), z.string().regex(phoneValidationPattern, { error: 'Invalid phone number.' })])
      .nullish()
      .transform(undefinedTransformer),
    username: itemStringValidator({ label: 'Username', min: 2, max: 255 }),
    password: itemStringValidator({ label: 'Password' }),
    confirmPassword: itemStringValidator({ label: 'Confirm password' }),
    agentId: itemStringValidator({ label: 'Agent' }),
    country: itemStringValidator({ label: 'Country', required: false }).nullable().transform(undefinedTransformer),
    region: itemStringValidator({ label: 'Region', required: false, transformer: transformer }).nullish(),
    isActive: z.boolean(),
    address: itemStringValidator({ label: 'Address', required: false, transformer: transformer }).nullish(),
    zip: itemStringValidator({
      label: 'Zip Code',
      required: false,
      max: 25,
      transformer: transformer,
    }).nullish(),
    city: itemStringValidator({ label: 'City', required: false, transformer: transformer }).nullish(),
    gender: z
      .enum([...GENDER_ENUM, ''])
      .nullish()
      .transform(undefinedTransformer),
    nationalId: itemStringValidator({
      label: 'National ID',
      required: false,
      transformer: transformer,
    }).nullish(),
    currency: z
      .enum([...CURRENCY_ENUM, ''])
      .nullish()
      .transform(undefinedTransformer),
    language: itemStringValidator({ label: 'Language', required: false, transformer: transformer }).nullish(),
  });

export const customerSchema = addCreatePasswordValidation(baseCustomerSchema(undefinedTransformer));
export const editCustomerSchema = addResetPasswordValidation(
  baseCustomerSchema(nullTransformer).extend({ agentId: z.string().nullish() })
).transform(({ username: _username, agentId: _agentId, ...customer }) => customer);

export type CustomerFormData = z.infer<typeof customerSchema>;

export const crmTaskSchema = z.object({
  scheduledTime: z.instanceof(dayjs as unknown as typeof Dayjs, { error: 'Date is required.' }),
  type: z.enum(CUSTOMER_TASK_MESSAGE_TYPES, { error: 'Message type is required.' }).transform(nullTransformer),
  comment: itemStringValidator({ label: 'Description', max: 300 }),
});

export type CRMTaskFormData = z.infer<typeof crmTaskSchema>;

export const addNewSpecialValueSchema = z.object({
  values: z.array(z.object({ id: z.string().min(1, { error: 'Value is required.' }) })),
  isActive: z.boolean(),
  participantId: itemIdValidator('Participant'),
  playerId: itemIdValidator('Player'),
});

export type AddNewSpecialValueFormData = z.infer<typeof addNewSpecialValueSchema>;

export const editableTableCellSchema = (isSpecialValueInput: boolean) =>
  z.object({
    value: isSpecialValueInput ? z.coerce.number() : z.coerce.number().min(1),
  });

export const resultsSchema = z.object({
  scores: z.array(
    z.object({
      eventPartName: z.string(),
      eventPartScoreId: z.string(),
      valueHome: z.number(),
      valueAway: z.number(),
    })
  ),
});

export type ResultsFormData = z.infer<typeof resultsSchema>;

export const conditionTypeSchema = z.object({
  name: itemNameValidator(300),
  restrictionMsgAdmin: itemStringValidator({
    label: 'Restriction Message Admin',
    max: 300,
    required: false,
  }).nullish(),
  verificationMsgAdmin: itemStringValidator({
    label: 'Verification Message Admin',
    max: 300,
    required: false,
  }).nullish(),
  restrictionMsgClient: itemStringValidator({
    label: 'Restriction Message Client',
    max: 300,
    required: false,
  }).nullish(),
  verificationMsgClient: itemStringValidator({
    label: 'Verification Message Client',
    max: 300,
    required: false,
  }).nullish(),
  description: itemInformationValidator,
});

export type ConditionTypesFormData = z.infer<typeof conditionTypeSchema>;

export const conditionGroupSchema = z.object({
  name: itemNameValidator(300),
  type: z.enum(['sport', 'competition', 'tournament', 'market_type', 'event', 'punter', 'ticket', 'agent']),
  description: itemInformationValidator,
});

export type ConditionGroupsFormData = z.infer<typeof conditionGroupSchema>;

export const conditionTemplateSchema = z
  .object({
    modelId: z.string().optional(),
    conditionGroupId: itemIdValidator('Condition Group').nullable(),
    type: z
      .enum(['sport', 'competition', 'tournament', 'market_type', 'event', 'punter', 'ticket', 'agent'])
      .optional(),
    conditions: z.array(
      z.object({
        conditionTypeId: itemIdValidator('Condition Type'),
        restrictionValue: condititionValueValidator,
        verificationValue: condititionValueValidator,
      })
    ),
  })
  .transform(({ modelId: _modelId, type: _type, ...template }) => template);

export type ConditionTemplateFormData = z.infer<typeof conditionTemplateSchema>;

export const conditionSchema = z.object({
  modelId: itemIdValidator('Model'),
  conditionGroupId: itemIdValidator('Condition Group').nullable(),
  type: z.enum(['sport', 'competition', 'tournament', 'market_type', 'event', 'punter', 'ticket', 'agent']),
  conditions: z.array(
    z.object({
      conditionTypeId: itemIdValidator('Condition Type'),
      restrictionValue: condititionValueValidator,
      verificationValue: condititionValueValidator,
    })
  ),
});

export type ConditionFormData = z.infer<typeof conditionSchema> & { product: string | null };

export const ticketReofferSchema = (maxStake: number) =>
  z.object({
    totalStakeAmount: z.coerce.number().max(maxStake),
    odds: z.record(z.string(), z.coerce.number().min(1)),
    specialValues: z.record(
      z.string(),
      z.array(
        z.object({
          id: z.string(),
          marketId: z.string(),
          name: z.string(),
          odds: z.string(),
        })
      )
    ),
  });

export const liabilitiesFormSchema = z.object({
  liabilities: z.array(
    z.object({
      name: z.string().min(1, { error: 'Name is required.' }),
      value: z.number(),
    })
  ),
});

export type LiabilitiesFormData = z.infer<typeof liabilitiesFormSchema>;

export const riskFactorsFormSchema = z.object({
  riskFactors: z.array(
    z.object({
      name: z.string().min(1, { error: 'Name is required.' }),
      value: z.number(),
    })
  ),
});

export type RiskFactorsFormData = z.infer<typeof riskFactorsFormSchema>;

export const riskManagementFormSchema = z.object({
  items: z.array(
    z.object({
      name: z.string().min(1, { error: 'Name is required.' }),
      value: z.string().min(1, { error: 'Value is required.' }),
    })
  ),
});

export type RiskManagementFormData = z.infer<typeof riskManagementFormSchema>;

export const commissionSchema = z
  .object({
    playerCommissionType: z.enum(['POSITION', 'PROFIT', 'PER_PICK']).optional(),
    playerCommissionPercentage: z.string().optional(),
    partnerCommissionType: z.enum(['POSITION', 'PROFIT', 'PER_PICK']).optional(),
    partnerCommissionPercentage: z.string().optional(),
    picks: z
      .object({
        '1': z.string(),
        '2': z.string(),
        '3': z.string(),
        '4-7': z.string(),
        '8-9': z.string(),
        '10+': z.string(),
      })
      .optional(),
    affiliateCommissionType: z.enum(['CPA', 'REVENUE_SHARE', 'HYBRID']).optional(),
    cpaAmount: z.string().optional(),
    holdPeriod: z.string().optional(),
    minDepositAmount: z.string().optional(),
    sportsbookWagering: z.string().optional(),
    casinoWagering: z.string().optional(),
    qualificationCriteria: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.playerCommissionType !== 'PER_PICK' || !data.picks) return true;

      return Object.values(data.picks).every((pick) => {
        if (!pick) return false;
        const num = Number(pick);
        return !isNaN(num) && num >= 0 && num <= 100;
      });
    },
    {
      error: 'All pick values must be numbers between 0 and 100 when commission type is PER_PICK',
      path: ['picks'],
    }
  )
  .superRefine((data, ctx) => {
    if (data.affiliateCommissionType !== 'CPA') return;

    const requiredFields = [
      { field: 'cpaAmount', label: 'CPA Amount' },
      { field: 'holdPeriod', label: 'Hold Period' },
    ] as const;

    requiredFields.forEach(({ field, label }) => {
      if (!data[field]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} is required`,
          path: [field],
        });
      }
    });
  });

export const crmUserSchema = z.object({
  firstName: z.array(z.string()),
  lastName: z.array(z.string()),
  fullName: z.array(z.string()),
  isActive: z.boolean(),
  country: z.array(z.string()),
  createdAt: z.object({
    from: z.custom<Dayjs>((val) => val instanceof dayjs, 'Invalid date').nullable(),
    to: z.custom<Dayjs>((val) => val instanceof dayjs, 'Invalid date').nullable(),
  }),
  phone: z.array(z.string()),
  email: z.array(z.string()),
  zip: z.array(z.string()),
  city: z.array(z.string()),
  region: z.array(z.string()),
  language: z.array(z.string()),
  age: z.object({
    from: z.coerce.number({ error: 'Range is required.' }).nullable(),
    to: z.coerce.number({ error: 'Range is required.' }).nullable(),
  }),
  roleTypes: z.array(z.string()),
});

export type CRMUserFilterField = keyof z.infer<typeof crmUserSchema>;

export const createCRMUserFilterSchema = (selectedFields: readonly CRMUserFilterField[]) => {
  const selectedFieldSet = new Set(selectedFields);

  return crmUserSchema.superRefine((data, ctx) => {
    if (selectedFieldSet.has('createdAt')) {
      if (!data.createdAt.from) {
        ctx.addIssue({ code: 'custom', path: ['createdAt', 'from'], message: 'Invalid date' });
      }

      if (!data.createdAt.to) {
        ctx.addIssue({ code: 'custom', path: ['createdAt', 'to'], message: 'Invalid date' });
      }
    }

    if (selectedFieldSet.has('age')) {
      if (data.age.from === null) {
        ctx.addIssue({ code: 'custom', path: ['age', 'from'], message: 'Range is required.' });
      }

      if (data.age.to === null) {
        ctx.addIssue({ code: 'custom', path: ['age', 'to'], message: 'Range is required.' });
      }
    }
  });
};

export const handleCreditsSchema = z.object({
  walletType: z.enum(['main', 'bonus'], { error: 'Wallet is required.' }),
  isOtherTransaction: z.boolean(),
  amount: z.coerce.number().positive({ error: 'Amount must be greater than 0' }),
  description: z.string().optional().transform(undefinedTransformer),
});

export const messagesFormSchema = z.object({
  group: z.string({ error: 'Field is required.' }).nullable(),
  topic: z.string().max(30, { error: 'Field must be 30 characters or fewer' }),
  text: z.string({ error: 'Field is required' }),
  partnerIds: z.array(z.string()).nullable(),
  playerIds: z.array(z.string()),
});

export type CreateMessageFormData = z.infer<typeof messagesFormSchema>;

export const jackpotFormSchema = z.object({
  name: itemNameValidator(300),
  product: itemStringValidator({ label: 'Product' }),
  isActive: z.boolean(),
  selectedStakeRangeIds: nonEmptyArray('Pay in amount is required.'),
  payInAmountRanges: z.array(
    z.object({
      id: z.string(),
      from: z.coerce.number().min(0.1, { error: 'Must be higher than 0.1' }),
      to: z.coerce
        .number()
        .nullish()
        .transform((val) => (val ? val : undefined)),
      contribution: z.coerce.number().positive({ error: 'Must be a positive number.' }),
    })
  ),
  winningTickets: nonEmptyArray('Winning bet range is required.'),
  winningLow: validateRequiredNumber('Winning range').min(0, { error: 'Winning amount cannot be negative.' }),
  winningHigh: validateRequiredNumber('Winning range').min(0, { error: 'Winning amount cannot be negative.' }),
  description: itemInformationValidator,
  icon: z.string(),
});

export const editJackpotFormSchema = jackpotFormSchema.pick({
  name: true,
  isActive: true,
  description: true,
  icon: true,
});

export type JackpotFormData = z.infer<typeof jackpotFormSchema>;

export const SportEventPartsSchema = z.object({
  sportEventParts: z.array(
    z.object({
      id: z.string(),
      eventPartName: z.string(),
      shortName: z.string().nullable(),
      isActive: z.boolean(),
      position: itemPositionValidator,
    })
  ),
});

export type SportEventPartsFormData = z.infer<typeof SportEventPartsSchema>;

export const marketMarginSchema = z.object({
  marketMargin: z.coerce.number().min(1, 'Must be between 1 and 100').max(100, 'Must be between 1 and 100'),
});

export type MarketMarginFormData = z.infer<typeof marketMarginSchema>;

export const bonusFilterSchema = z.object({
  product: z.enum(generateZodEnumIds(BONUS_PRODUCT_TYPE_OPTIONS)),
  validityPeriod: z.enum(generateZodEnumIds(VALIDITY_PERIOD_OPTIONS)),
  fromTimestamp: z.custom<Dayjs>((val) => val instanceof dayjs, 'Invalid date').nullish(),
  toTimestamp: z.custom<Dayjs>((val) => val instanceof dayjs, 'Invalid date').nullish(),
  schedule: z.enum(generateZodEnumIds(BONUS_TIMEFRAME_OPTIONS)),
});

export type BonusFilterFormData = z.infer<typeof bonusFilterSchema>;

export const bonusFormSchema = z.object({
  name: itemNameValidator().pipe(z.string()),
  cashBackBonusEligibility: z.string(),
  status: z.enum([
    ...generateZodEnumIds(BONUS_STATUS_OPTIONS_CUSTOM),
    ...generateZodEnumIds(BONUS_STATUS_OPTIONS_ONGOING),
  ]),
  refundPercentage: validateRequiredNumber('Refund percentage')
    .min(1, 'Must be between 1 and 100')
    .max(100, 'Must be between 1 and 100'),
  minimumLostAmount: validateRequiredNumber('Min. lost amount'),
  maximumRefundAmount: validateRequiredNumber('Max. refund amount'),
  userIds: z.array(z.string()),
  description: itemInformationValidator,
  ...bonusFilterSchema.shape,
});

export const affiliateBonusFormSchema = z
  .object({
    campaignBonusType: z.string(),
    rewardType: z.enum(generateZodEnumIds(AFFILIATE_BONUS_REWARD_TYPE_OPTIONS)),
    bonusCategory: z.enum(generateZodEnumIds(AFFILIATE_BONUS_CATEGORY_OPTIONS)),
    bonusPercentage: nullableNumberInput(),
    fixedAmount: nullableNumberInput(),
    capLimit: z.coerce
      .number()
      .min(0, 'Must be at least 0')
      .max(MAX_CAMPAIGN_AMOUNT, `Must be at most ${MAX_CAMPAIGN_AMOUNT.toLocaleString()}`),
    minDepositAmount: z.coerce
      .number()
      .min(1, 'Must be at least 1')
      .max(MAX_CAMPAIGN_AMOUNT, `Must be at most ${MAX_CAMPAIGN_AMOUNT.toLocaleString()}`),
    timeframe: z.coerce
      .number()
      .min(1, 'Must be at least 1')
      .max(MAX_CAMPAIGN_PERCENTAGE, `Must be at most ${MAX_CAMPAIGN_PERCENTAGE.toLocaleString()}`),
    sbWagering: nullableNumberInput().refine(
      (value) => value === null || (value >= 1 && value <= MAX_CAMPAIGN_AMOUNT),
      {
        error: `Must be between 1 and ${MAX_CAMPAIGN_AMOUNT.toLocaleString()}`,
      }
    ),
    casinoWagering: z.coerce
      .number()
      .min(1, 'Must be at least 1')
      .max(MAX_CAMPAIGN_AMOUNT, `Must be at most ${MAX_CAMPAIGN_AMOUNT.toLocaleString()}`),
  })
  .superRefine((data, ctx) => {
    const isPercentage = data.bonusCategory === 'PERCENTAGE';

    if (isPercentage) {
      if (data.bonusPercentage == null) {
        ctx.addIssue({
          path: ['bonusPercentage'],
          message: 'Bonus percentage is required.',
          code: z.ZodIssueCode.custom,
        });
      } else if (data.bonusPercentage < 0 || data.bonusPercentage > MAX_CAMPAIGN_PERCENTAGE) {
        ctx.addIssue({
          path: ['bonusPercentage'],
          message: `Must be between 0 and ${MAX_CAMPAIGN_PERCENTAGE.toLocaleString()}`,
          code: z.ZodIssueCode.custom,
        });
      }
    } else {
      if (data.fixedAmount == null) {
        ctx.addIssue({
          path: ['fixedAmount'],
          message: 'Fixed amount is required.',
          code: z.ZodIssueCode.custom,
        });
      } else if (data.fixedAmount < 0 || data.fixedAmount > MAX_CAMPAIGN_AMOUNT) {
        ctx.addIssue({
          path: ['fixedAmount'],
          message: `Must be between 0 and ${MAX_CAMPAIGN_AMOUNT.toLocaleString()}`,
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });

export type AffiliateBonusFormData = z.infer<typeof affiliateBonusFormSchema>;

export const selectionBonusFormSchema = bonusFormSchema
  .omit({
    name: true,
    refundPercentage: true,
    minimumLostAmount: true,
    maximumRefundAmount: true,
    cashBackBonusEligibility: true,
    fromTimestamp: true,
    toTimestamp: true,
    schedule: true,
  })
  .extend({
    name: z.string(),
    minReqOdd: z.coerce.number().min(1),
    bonusPercentagePerSelection: z.array(
      z.object({
        value: z.coerce.number().optional().nullable(),
      })
    ),
    numOfSelectionsBonusEligibility: z.string(),
  });

export type SelectionBonusFormData = z.infer<typeof selectionBonusFormSchema>;

export type BonusFormData = z.infer<typeof bonusFormSchema>;

export const welcomeBonusFilterSchema = z.object({
  product: z.enum(generateZodEnumIds(WELCOME_BONUS_PRODUCT_TYPE_OPTIONS)).optional(),
  welcomeBonusScope: z.enum(generateZodEnumIds(WELCOME_BONUS_SCOPE_OPTIONS)).optional(),
  validityPeriod: z.enum(generateZodEnumIds(WELCOME_BONUS_VALIDITY_PERIOD_OPTIONS)).optional(),
  timeframe: z.coerce.number().min(0, 'Must be at least 0').optional(),
});

export type WelcomeBonusFilterFormData = z.infer<typeof welcomeBonusFilterSchema>;

export const marketplaceFormSchema = z.object({
  name: itemNameValidator(),
  domains: z
    .array(
      z.object({
        domain: itemStringValidator({ label: 'Domain' }),
        branding: z.object({
          skin: itemStringValidator({ label: 'Skin' }),
          theme: itemStringValidator({ label: 'Theme' }),
        }),
      })
    )
    .min(1, 'At least one domain is required.'),
  country: itemStringValidator({ label: 'Country' }),
  status: itemStringValidator({ label: 'Status' }),
  contactInformation: z.object({
    contactName: itemStringValidator({ label: 'Contact name' }),
    contactEmail: z.email({ error: 'Invalid contact email.' }),
    contactPhoneNumber: itemStringValidator({ label: 'Contact phone' }),
  }),
  sanityConfig: z
    .object({
      projectId: z.string().regex(/^[a-z0-9-]+$/, 'Invalid Project ID.'),
      dataset: z.string().regex(/^[~]?[a-z0-9_-]{1,63}$/, 'Invalid dataset.'),
    })
    .optional(),
});

export type MarketplaceFormData = z.infer<typeof marketplaceFormSchema>;

const signUpFormFieldSchema = z.object({
  name: z.string(),
  order: z.number(),
  required: z.boolean(),
});

export const marketplaceConfigSchema = z
  .object({
    model: itemStringValidator({ label: 'Model' }),
    paymentGateway: itemStringValidator({ label: 'Payment Gateway' }).nullish(),
    cryptoProvider: z.enum(['moralis', 'zero_x_processing']).default('moralis'),
    moralis: z
      .object({ depositEnabled: z.boolean().default(true), withdrawalEnabled: z.boolean().default(true) })
      .default({ depositEnabled: true, withdrawalEnabled: true }),
    zeroXProcessing: z
      .object({
        enabled: z.boolean().default(false),
        depositEnabled: z.boolean().default(false),
        withdrawalEnabled: z.boolean().default(false),
      })
      .default({ enabled: false, depositEnabled: false, withdrawalEnabled: false }),
    products: z.object({
      sportsbook: z.boolean(),
      casino: z.boolean(),
    }),
    features: z.object({
      paymentSettings: z.boolean(),
    }),
    currency: itemStringValidator({ label: 'Currency' }),
    languageConfig: z.object({
      defaultLanguage: itemStringValidator({ label: 'Language' }),
      supportedLanguages: z.array(itemStringValidator({ label: 'Supported languages' })).optional(),
    }),
    licenseInformation: itemStringValidator({ label: 'License Information', required: false }).nullable(),
    defaultRegistrationAgentId: z.preprocess((value) => (value === '' ? null : value), z.uuid().nullable().optional()),
    registrationFlow: z.object({
      signUpFormFields: z.array(signUpFormFieldSchema),
      emailVerification: itemStringValidator({ label: 'Email verification' }),
    }),
    googleAnalyticsId: itemStringValidator({ label: 'Google Analytics ID', required: false }).nullable(),
    openReplayProjectKey: itemStringValidator({ label: 'OpenReplay Project Key', required: false }).nullable(),
    maintenance: z
      .object({
        underMaintenance: z.boolean(),
        message: z.record(z.string(), z.string()),
      })
      .optional(),
    systems: z.object({
      affiliate: z.boolean(),
    }),
    kyc: z
      .object({
        enabled: z.boolean(),
        trigger: z.enum(['registration', 'firstDeposit', 'firstWithdrawal']).optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.paymentGateway === PAYMENT_GATEWAY.FINPSP && data.currency !== FINPSP_CURRENCY) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'FINPSP requires BDT currency',
        path: ['currency'],
      });
    }
    if (data.kyc?.enabled && !data.kyc.trigger) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'KYC Verification Flow is required',
        path: ['kyc', 'trigger'],
      });
    }
  });

export type MarketplaceConfigFormData = z.infer<typeof marketplaceConfigSchema>;

export const sportsbookConfigSchema = z.object({
  oddsFormat: z.enum(ODDS_FORMAT),
  reofferOddsDisplay: z.enum(REOFFER_ODDS_DISPLAY),
  feedSource: itemStringValidator({ label: 'Feed Source' }),
  ftbTicketValidation: z.boolean(),
  assetVariants: z
    .object({
      sportIcons: z.enum(SPORT_ICON_VARIANT).optional(),
      competitionsFlags: z.enum(COMPETITIONS_FLAG_VARIANT).optional(),
      teamAssets: z.enum(TEAM_ASSETS_VARIANT).optional(),
    })
    .optional(),
  AdditionalFeatures: z.object({
    BonusesEngine: z.boolean(),
    JackpotEngine: z.boolean(),
  }),
  widgetFeatures: z.object({
    preMatchWidget: z.boolean(),
    liveWidget: z.boolean(),
  }),
  betBuilder: z.boolean().default(false),
});

export type SportsbookConfigFormData = z.infer<typeof sportsbookConfigSchema>;

export const casinoConfigSchema = z.object({
  requiresAuthentication: z.boolean().default(false),
  layoutTemplate: itemStringValidator({ label: 'Layout Template' }),
  jackpotEnabled: z.boolean().default(false),
});

export type CasinoConfigFormData = z.infer<typeof casinoConfigSchema>;

const casinoMarketplaceRequiredField = (label: string) =>
  z
    .string({ error: `${label} is required.` })
    .trim()
    .min(1, `${label} is required.`);

export const casinoMarketplaceFormSchema = z
  .object({
    name: casinoMarketplaceRequiredField('Marketplace name'),
    currency: casinoMarketplaceRequiredField('Currency'),
    language: casinoMarketplaceRequiredField('Language'),
    aggregators: z.array(casinoMarketplaceRequiredField('Aggregator')).min(1, 'Aggregator is required.'),
    freeSpinsAggregator: z.string().optional(),
    gameType: z.array(casinoMarketplaceRequiredField('Game type')).min(1, 'Game type is required.'),
    apiDomain: casinoMarketplaceRequiredField('Api domain'),
    url: casinoMarketplaceRequiredField('Url'),
    slug: casinoMarketplaceRequiredField('Slug'),
    minimumStakePerBet: casinoMarketplaceRequiredField('Minimum Stake Per Bet'),
    maximumStakePerBet: casinoMarketplaceRequiredField('Maximum Stake Per Bet'),
  })
  .superRefine((values, ctx) => {
    if (
      values.aggregators.includes('GGA') &&
      values.aggregators.includes('GOLDGAMES') &&
      !['GGA', 'GOLDGAMES'].includes(values.freeSpinsAggregator ?? '')
    ) {
      ctx.addIssue({ code: 'custom', path: ['freeSpinsAggregator'], message: 'Choose the free spins aggregator.' });
    }
  });

export type CasinoMarketplaceFormData = z.infer<typeof casinoMarketplaceFormSchema>;

const casinoCategoryLocaleValueSchema = z.object({
  name: z
    .string()
    .trim()
    .max(50, { error: 'Must be 50 characters or fewer.' })
    .optional()
    .transform(undefinedTransformer),
  providerCategoryName: z
    .string()
    .trim()
    .max(50, { error: 'Must be 50 characters or fewer.' })
    .optional()
    .transform(undefinedTransformer),
});

export const casinoCategoryFormSchema = z.object({
  name: itemNameValidator(50),
  providerCategoryName: z
    .string()
    .trim()
    .max(50, { error: 'Must be 50 characters or fewer.' })
    .optional()
    .transform(undefinedTransformer),
  locale: z.record(z.string(), casinoCategoryLocaleValueSchema).optional(),
});

export type CasinoCategoryFormSchemaData = z.infer<typeof casinoCategoryFormSchema>;

const casinoBooleanStringSchema = (label: string) =>
  z.string().refine((value) => value === 'true' || value === 'false', { error: `${label} is required.` });

export const casinoGameFormSchema = z.object({
  image: z.any().optional(),
  name: itemNameValidator(50),
  categoryId: itemStringValidator({ label: 'Category', max: 50 }),
  demoAvailable: casinoBooleanStringSchema('Demo Available'),
  frbAvailable: casinoBooleanStringSchema('Frb Available'),
});

export type CasinoGameFormSchemaData = z.infer<typeof casinoGameFormSchema>;

const basePartnerSchema = z.object({
  firstName: itemStringValidator({ label: 'First name' }),
  lastName: itemStringValidator({ label: 'Last name' }),
  email: itemEmailValidator,
  groupName: z.enum(USER_ROLES, { error: 'Role is required' }),
  managerId: itemStringValidator({ label: 'Manager' }).optional(),
  products: z
    .object({
      sportsbook: z.boolean(),
      casino: z.boolean(),
    })
    .optional(),
});

export const createPartnerSchema = (isUserManager: boolean) =>
  addCreatePasswordValidation(basePartnerSchema).superRefine((data, ctx) => {
    // Skip validation if the user is a manager - they can only create their own agents
    if (isUserManager) {
      return;
    }

    // Only validate managerId if creating an agent or affiliate
    const roleToCreate = data.groupName;
    const requiresManager = roleRequiresManager(roleToCreate);
    if (!requiresManager) {
      return;
    }

    // Require selecting a manager when an admin is creating an agent or affiliate
    if (!data.managerId) {
      ctx.addIssue({
        path: ['managerId'],
        message: 'Manager is required.',
        code: z.ZodIssueCode.custom,
      });
    }
  });

export const editPartnerSchema = addResetPasswordValidation(basePartnerSchema).transform(
  ({ managerId: _managerId, ...partner }) => partner
);

export type PartnerFormData = z.infer<ReturnType<typeof createPartnerSchema>>;
export type EditPartnerFormData = z.infer<typeof editPartnerSchema>;

// Keep this symbol set aligned with validator.js isStrongPassword, used by the API DTO.
const superAdminPasswordSymbolRegex = /[-#!$@£%^&*()_+|~=`{}[\]:";'<>?,./\\ ]/;

const superAdminPasswordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters.')
  .max(16, 'Password must be 16 characters or fewer.')
  .regex(/[a-z]/, 'Password must include at least one lowercase letter.')
  .regex(/[A-Z]/, 'Password must include at least one uppercase letter.')
  .regex(/\d.*\d/, 'Password must include at least two digits.')
  .regex(superAdminPasswordSymbolRegex, 'Password must include at least one special character.');

export const createSuperAdminAccountSchema = z
  .object({
    firstName: z.string().trim().min(1, 'First name is required.').max(255),
    lastName: z.string().trim().min(1, 'Last name is required.').max(255),
    email: z.string().trim().toLowerCase().pipe(z.email('Invalid email.').max(255)),
    password: superAdminPasswordSchema,
    confirmPassword: z.string().min(1, 'Confirm password is required.'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    error: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type CreateSuperAdminAccountFormData = z.infer<typeof createSuperAdminAccountSchema>;

const paymentFieldSchema = z.coerce
  .number()
  .nullable()
  .optional()
  .transform((value) => (value === 0 ? null : value));

export const paymentSettingsSchema = z.object({
  minAmountPerTransaction: paymentFieldSchema,
  maxAmountPerTransaction: paymentFieldSchema,
  maxDailyAmount: paymentFieldSchema,
  maxWeeklyAmount: paymentFieldSchema,
  maxMonthlyAmount: paymentFieldSchema,
  maxDailyCount: paymentFieldSchema,
  maxWeeklyCount: paymentFieldSchema,
  maxMonthlyCount: paymentFieldSchema,
  approvalMode: z.enum(APPROVAL_MODE).optional(),
});

export type PaymentSettingsFormData = z.infer<typeof paymentSettingsSchema>;

export { affiliateCommissionSchema } from './affiliateCommissionSchema';
